// Report Builder template (de)serialisation helpers and the browser-local fallback store.
// The server stores `{ name, template, page_size, orientation }` (backend/reports_api.py);
// the builder works with `{ pageSize: 'a4'|'letter'|'a3', landscape, elements }`.

export const MAX_TEMPLATE_BYTES = 512 * 1024; // mirrors backend MAX_TEMPLATE_BYTES
export const LOCAL_KEY = 'ugis.reportTemplates.v1';

// Builder page-size keys <-> the labels the API accepts.
const KEY_TO_LABEL = { a4: 'A4', letter: 'Letter', a3: 'A3' };
const LABEL_TO_KEY = { A4: 'a4', Letter: 'letter', A3: 'a3' };

/** Elements as stored: map snapshots are regenerated on load, so they are not persisted. */
export function serializeElements(elements) {
  return (elements || []).map((e) => ({ ...e, src: e.type === 'map' ? null : e.src }));
}

/** Builder state -> API body fields (no name / org / project). */
export function serializeTemplate({ pageSize, landscape, elements }) {
  return {
    template: { pageSize, landscape: !!landscape, elements: serializeElements(elements) },
    page_size: KEY_TO_LABEL[pageSize] || 'A4',
    orientation: landscape ? 'landscape' : 'portrait',
  };
}

/** API row (or a bare template document) -> builder state. Tolerates missing fields. */
export function deserializeTemplate(row) {
  const doc = (row && row.template && typeof row.template === 'object' ? row.template : row) || {};
  const pageSize =
    (typeof doc.pageSize === 'string' && KEY_TO_LABEL[doc.pageSize] && doc.pageSize) ||
    LABEL_TO_KEY[row?.page_size] ||
    'a4';
  let landscape = true;
  if (typeof doc.landscape === 'boolean') landscape = doc.landscape;
  else if (row?.orientation) landscape = row.orientation === 'landscape';
  const elements = Array.isArray(doc.elements) ? doc.elements.filter((e) => e && typeof e === 'object') : [];
  return { pageSize, landscape, elements };
}

/** Highest numeric suffix among `el-N` ids, so new elements never reuse a loaded id. */
export function maxElementId(elements) {
  let max = 0;
  (elements || []).forEach((e) => {
    const m = /^el-(\d+)$/.exec(e?.id || '');
    if (m) max = Math.max(max, Number(m[1]));
  });
  return max;
}

/** UTF-8 byte length of the JSON document, to warn before the server's 512 KB limit. */
export function templateBytes(template) {
  const json = JSON.stringify(template);
  if (typeof TextEncoder !== 'undefined') return new TextEncoder().encode(json).length;
  return unescape(encodeURIComponent(json)).length; // no TextEncoder (old jsdom): UTF-8 via percent-encoding
}

export function templateTooLarge(template) {
  return templateBytes(template) > MAX_TEMPLATE_BYTES;
}

// ── browser-local store (fallback; also the source for "Upload local templates") ─────

function storage() {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null;
  }
}

/** [{ id, name, savedAt, ...serializeTemplate() }] — [] when storage is unavailable or corrupt. */
export function readLocalTemplates() {
  const s = storage();
  if (!s) return [];
  try {
    const list = JSON.parse(s.getItem(LOCAL_KEY) || '[]');
    return Array.isArray(list) ? list.filter((t) => t && t.name && t.template) : [];
  } catch {
    return [];
  }
}

function writeLocalTemplates(list) {
  const s = storage();
  if (!s) return false;
  try {
    s.setItem(LOCAL_KEY, JSON.stringify(list));
    return true;
  } catch {
    return false;
  }
}

/** Save (or replace by name) a local template. Returns false when the browser refused. */
export function saveLocalTemplate(name, state, now = () => new Date().toISOString()) {
  const rest = readLocalTemplates().filter((t) => t.name !== name);
  const entry = { id: `local-${name}`, name, savedAt: now(), ...serializeTemplate(state) };
  return writeLocalTemplates([...rest, entry]);
}

export function removeLocalTemplate(id) {
  return writeLocalTemplates(readLocalTemplates().filter((t) => t.id !== id));
}

export function clearLocalTemplates() {
  return writeLocalTemplates([]);
}
